import { yupResolver } from '@hookform/resolvers/yup';
import React, { forwardRef, useCallback, useImperativeHandle, useMemo, useState } from 'react';
import {
  Control,
  SubmitErrorHandler,
  UseFieldArrayRemove,
  UseFieldArrayReplace,
  UseFormGetValues,
  UseFormRegister,
  UseFormSetValue,
  useFieldArray,
  useForm,
  useWatch,
} from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import {
  ProductionMasters,
  TossMaterial,
  TossMaterialCategoryTree,
  TossMaterialFile,
  TossMaterialGroup,
  TossMaterialSubgroup,
  TossMaterialTargetManual,
} from '../../../../../api/model';
import yup from '../../../../../utils/custom-yup';
import { CheckBox } from '../../../../atoms/CheckBox';
import DatePicker from '../../../../atoms/DatePicker';
import NoDataComponent from '../../../../atoms/NoDataComponent';
import SingleSelect from '../../../../atoms/SingleSelect';
import SlideToggle from '../../../../modules/SlideToggle';
import { DroppableTd, InnerTabCommonNavi } from './CommonComponents';

interface ServiceSpecTrProps {
  productionId: number; // 制作管理 ID
  i: number; // トス資料のインデックス 1
  j: number; // トス資料のインデックス 2
  k: number; // トス資料のインデックス 3
  tossMaterial: TossMaterial; // 表示対象のトス資料
  editable: boolean; // 編集モードかどうか
  tossMaterialTargetManuals: TossMaterialTargetManual[]; // 対象マニュアルのマスターデータ
  setValue: UseFormSetValue<{
    toss_materials: TossMaterial[][][];
  }>; // useForm の返す setValue
  getValues: UseFormGetValues<{
    toss_materials: TossMaterial[][][];
  }>; // useForm の返す getValues
  register: UseFormRegister<{
    toss_materials: TossMaterial[][][];
  }>; // useForm の返す register
  control: Control<{
    toss_materials: TossMaterial[][][];
  }>; // useForm の返す control
  replace: UseFieldArrayReplace<
    {
      toss_materials: TossMaterial[][][];
    },
    `toss_materials.${number}.${number}`
  >; // useFieldArray の返す replace
  remove: UseFieldArrayRemove; // useFieldArray の返す remove
}

/**
 * トス資料の表の各行
 * @param param0
 * @returns
 */
function ServiceSpecTr({
  productionId,
  i,
  j,
  k,
  tossMaterial,
  editable,
  tossMaterialTargetManuals,
  setValue,
  getValues,
  register,
  control,
  replace,
  remove,
}: ServiceSpecTrProps) {
  // コメントの値をリアルタイムに取得
  const comment = useWatch({ control, name: `toss_materials.${i}.${j}.${k}.comment` });

  // file1 の値をリアルタイムに取得
  const file1 = useWatch({ control, name: `toss_materials.${i}.${j}.${k}.file1` });

  // file2 の値をリアルタイムに取得
  const file2 = useWatch({ control, name: `toss_materials.${i}.${j}.${k}.file2` });

  // ファイルドロップが成功した場合の処理
  const onDropSucceeded = useCallback(
    (files: TossMaterialFile[], setted: boolean, name: 'file1' | 'file2') => {
      // 現在表示されているトス資料の情報
      const prevTossMaterials = getValues().toss_materials[i][j];

      // 次に設定するトス資料情報
      const nextTossMaterials: TossMaterial[] = [];

      if (setted) {
        // もともとその行にファイルが設定してあった場合

        // そのまま表示するデータ
        for (let l = 0; l <= k; l += 1) {
          nextTossMaterials.push(prevTossMaterials[l]);
        }

        // アップロードされたファイルを順番に表示する
        files.forEach((w) => {
          const newTossMaterial: TossMaterial = {
            ...prevTossMaterials[k],
          };
          newTossMaterial[name] = w;

          // ファイルはコピーしない
          newTossMaterial[name === 'file1' ? 'file2' : 'file1'] = undefined;

          // ファイルがアップロードされた行以外は ID、最終更新日、更新者をリセット
          newTossMaterial.toss_material_id = undefined;
          newTossMaterial.updated_on = undefined;
          newTossMaterial.update_user_name = undefined;

          nextTossMaterials.push(newTossMaterial);
        });

        // 下にずらして表示するデータ
        for (let l = k + 1; l < prevTossMaterials.length; l += 1) {
          nextTossMaterials.push(prevTossMaterials[l]);
        }
      } else {
        // もともとその行にはファイルが設定していなかった場合

        // そのまま表示するデータ
        for (let l = 0; l < k; l += 1) {
          nextTossMaterials.push(prevTossMaterials[l]);
        }

        // アップロードされたファイルを順番に表示する
        files.forEach((w, l) => {
          const newTossMaterial: TossMaterial = {
            ...prevTossMaterials[k],
          };
          newTossMaterial[name] = w;
          if (l !== 0) {
            // ファイルはコピーしない
            newTossMaterial[name === 'file1' ? 'file2' : 'file1'] = undefined;

            // ファイルがアップロードされた行以外は ID、最終更新日、更新者をリセット
            newTossMaterial.toss_material_id = undefined;
            newTossMaterial.updated_on = undefined;
            newTossMaterial.update_user_name = undefined;
          }
          nextTossMaterials.push(newTossMaterial);
        });

        // 下にずらして表示するデータ
        for (let l = k + 1; l < prevTossMaterials.length; l += 1) {
          nextTossMaterials.push(prevTossMaterials[l]);
        }
      }
      replace(nextTossMaterials);
    },
    [getValues, i, j, k, replace]
  );

  // ファイル名を変更する場合の処理
  const onChangeMaterialFileName = useCallback(
    (next: string | undefined, name: 'file1' | 'file2') =>
      setValue(`toss_materials.${i}.${j}.${k}.${name}.file_name`, next ?? ''),
    [i, j, k, setValue]
  );

  // ファイル削除する場合の処理
  const onClickDelete = useCallback(
    (name: 'file1' | 'file2') => {
      // ファイルを空に
      setValue(`toss_materials.${i}.${j}.${k}.${name}`, undefined);
    },
    [setValue, i, j, k]
  );

  // 行削除する場合の処理
  const onClickDeleteRow = useCallback(() => remove(k), [remove, k]);

  // タブ内のトス資料名を全て取得する
  const getPresentFilenames = useCallback(() => {
    const values = getValues();
    const filenames = values.toss_materials
      .flat()
      .flat()
      .flat()
      .flatMap((x) => {
        if (x.file1?.file_name != null && x.file2?.file_name != null) {
          return [x.file1?.file_name, x.file2?.file_name];
        }
        if (x.file1?.file_name != null) {
          return x.file1?.file_name;
        }
        if (x.file2?.file_name != null) {
          return x.file2?.file_name;
        }
        return [];
      });
    return filenames;
  }, [getValues]);

  // 一列目の内容（表によって異なる）
  const column1Contents =
    i < 3 ? (
      <CheckBox
        label={undefined}
        name={`toss_materials.${i}.${j}.${k}.check`}
        isDisabled={!editable}
        register={register}
        value={undefined}
      />
    ) : (
      <SingleSelect
        name={`toss_materials.${i}.${j}.${k}.toss_material_target_manual_id`}
        allowEmpty={false}
        options={tossMaterialTargetManuals}
        getValue={(w) => w.toss_material_target_manual_id}
        getLabel={(w) => w.target_manual_name}
        isDisabled={!editable}
        register={register}
      />
    );

  return (
    <tr>
      <td>{column1Contents}</td>
      <td>{tossMaterial.sp}</td>
      {/* コメント */}
      <td>
        {/* 5文字ごとに改行を入れる */}
        {(comment ?? '').split('').map((z, l) => (
          // eslint-disable-next-line react/no-array-index-key
          <React.Fragment key={l}>
            {l > 0 && l % 20 === 0 ? <br /> : null}
            {z}
          </React.Fragment>
        ))}
      </td>
      <td>
        <DatePicker
          name={`toss_materials.${i}.${j}.${k}.issued_at`}
          isDisabled={!editable}
          control={control}
          displayMark
        />
      </td>
      {/* トス資料ファイル1 */}
      <DroppableTd
        productionId={productionId}
        tabIndex={2 as number}
        editable={editable}
        file={file1}
        comment={comment}
        getPresentFilenames={getPresentFilenames}
        onDropSucceeded={(files) => onDropSucceeded(files, file1 != null, 'file1')}
        onChangeComment={(next) => {
          setValue(`toss_materials.${i}.${j}.${k}.comment`, next);
        }}
        onChangeMaterialFileName={(next) => onChangeMaterialFileName(next, 'file1')}
        onClickDelete={() => onClickDelete('file1')}
        onCickDeleteRow={
          tossMaterial.is_initial === true || file1 != null || file2 != null ? undefined : onClickDeleteRow
        }
      />
      {/* トス資料ファイル2 */}
      <DroppableTd
        productionId={productionId}
        tabIndex={2 as number}
        editable={editable}
        file={file2}
        comment={undefined}
        getPresentFilenames={getPresentFilenames}
        onDropSucceeded={(files) => onDropSucceeded(files, file2 != null, 'file2')}
        onChangeComment={undefined}
        onChangeMaterialFileName={(next) => onChangeMaterialFileName(next, 'file2')}
        onClickDelete={() => onClickDelete('file2')}
        onCickDeleteRow={
          tossMaterial.is_initial === true || file1 != null || file2 != null ? undefined : onClickDeleteRow
        }
      />
      <td>{tossMaterial.updated_on?.replaceAll('-', '/')}</td>
      <td>{tossMaterial.update_user_name}</td>
    </tr>
  );
}

interface ServiceSpecTabTableProps {
  productionId: number; // 制作管理 ID
  productionCategoryId: number; // 制作管理カテゴリー ID
  tossMaterialGroupId: number; // トス資料グループ ID
  editable: boolean; // 編集モードかどうか
  tossMaterialSubGroup: TossMaterialSubgroup; // 表示対象のトス資料サブグループ
  i: number; // トス資料サブグループのインデックス 1
  j: number; // トス資料サブグループのインデックス 2
  tossMaterialTargetManuals: TossMaterialTargetManual[]; // 対象マニュアルのマスターデータ
  register: UseFormRegister<{
    toss_materials: TossMaterial[][][];
  }>; // useForm の返す register
  setValue: UseFormSetValue<{
    toss_materials: TossMaterial[][][];
  }>; // useForm の返す setValue
  getValues: UseFormGetValues<{
    toss_materials: TossMaterial[][][];
  }>; // useForm の返す getValues
  control: Control<{
    toss_materials: TossMaterial[][][];
  }>; // useForm の返す control
}

/**
 * サービススペックタブのトス資料の表
 * @param param0
 * @returns
 */
function ServiceSpecTabTable({
  productionId,
  productionCategoryId,
  tossMaterialGroupId,
  editable,
  tossMaterialSubGroup,
  i,
  j,
  tossMaterialTargetManuals,
  register,
  setValue,
  getValues,
  control,
}: ServiceSpecTabTableProps) {
  // 言語情報
  const { t } = useTranslation();

  // フォームが表になる場合に利用
  const { fields, append, remove, replace } = useFieldArray({
    control,
    name: `toss_materials.${i}.${j}`,
  });

  // テーブルによって異なるヘッダー

  // 一列目のヘッダー
  const columnHeader1 = useMemo(() => {
    let response = t('Check');
    if (i === 3) {
      response = t('対象マニュアル');
    }
    return response;
  }, [t, i]);

  // 三列目のヘッダー
  const columnHeader3 = useMemo(() => {
    let response = t('SV Dept...');
    if (i === 2) {
      response = t('その他');
    }
    if (i === 3) {
      response = t('連絡書サンプル');
    }
    return response;
  }, [t, i]);

  // 四列目のヘッダー
  const columnHeader4 = useMemo(() => {
    let response = t('D Dept');
    if (i === 2) {
      response = t('その他');
    }
    if (i === 3) {
      response = t('連絡書類　HGT発行資料');
    }
    return response;
  }, [t, i]);

  return (
    <>
      {/* サブグループ名 */}
      <p className="msrttl">{tossMaterialSubGroup.subgroup_name}</p>
      {/* 新規追加ボタン */}
      <p className="newadd">
        <button
          type="button"
          className="text-button"
          onClick={() => {
            if (!editable) return;

            // 新規追加した行の初期値を設定する
            const newTossMaterial: TossMaterial = {
              toss_material_id: undefined,
              toss_material_production_category_id: productionCategoryId,
              toss_material_group_id: tossMaterialGroupId,
              toss_material_subgroup_id: tossMaterialSubGroup.toss_material_subgroup_id,
              toss_material_sending_material_id: undefined,
              toss_material_material_name_id: undefined,
              check: false,
              toss_material_target_manual_id: i === 3 ? 1 : undefined,
              sp: i === 3 ? undefined : 'その他',
              rev: undefined,
              toss_material_schedule_id: undefined,
              comment: undefined,
              is_initial: false,
              sort_order: 0,
              file1: undefined,
              file2: undefined,
              share_destination_productions: undefined,
              update_user_name: undefined,
              updated_on: undefined,
              issued_at: undefined,
            };
            append(newTossMaterial);
          }}
          disabled={!editable}
        >
          <i className="fa-solid fa-circle-plus" />
          {t('新規追加')}
        </button>
      </p>
      {/* トス資料の表 */}
      <table className="table47">
        <tbody>
          <tr>
            <th style={i === 3 ? { width: 'auto' } : {}}>{columnHeader1}</th>
            <th>{t('SP')}</th>
            <th>{t('コメント')}</th>
            <th className="data">{t('date')}</th>
            <th>{columnHeader3}</th>
            <th>{columnHeader4}</th>
            <th>{t('最終更新日')}</th>
            <th>{t('更新者')}</th>
          </tr>
          {fields.map((z, k) => (
            <ServiceSpecTr
              key={z.id}
              productionId={productionId}
              i={i}
              j={j}
              k={k}
              tossMaterial={z}
              editable={editable}
              tossMaterialTargetManuals={tossMaterialTargetManuals}
              setValue={setValue}
              getValues={getValues}
              register={register}
              control={control}
              replace={replace}
              remove={remove}
            />
          ))}
        </tbody>
      </table>
    </>
  );
}

interface ServiceSpecTabGroupProps {
  productionId: number; // 制作管理 ID
  productionCategoryId: number; // 制作管理カテゴリ ID
  tossMaterialGroup: TossMaterialGroup; // 表示対象のトス資料グループ
  i: number; // トス資料グループのインデックス
  editable: boolean; // 編集モードかどうか
  tossMaterialTargetManuals: TossMaterialTargetManual[]; // 対象マニュアルマスターデータ
  open: boolean; // 開いているかどうか
  toggleOpen: () => void; // 開閉実行
  setValue: UseFormSetValue<{
    toss_materials: TossMaterial[][][];
  }>; // useForm で返る setValus
  getValues: UseFormGetValues<{
    toss_materials: TossMaterial[][][];
  }>; // useForm で返る getValues
  register: UseFormRegister<{
    toss_materials: TossMaterial[][][];
  }>; // useForm で返る register
  control: Control<{
    toss_materials: TossMaterial[][][];
  }>; // useForm で返る control
}

/**
 * サービススペックタブのトス資料グループの表示
 * @param param0
 * @returns
 */
function ServiceSpecTabGroup({
  productionId,
  productionCategoryId,
  tossMaterialGroup,
  i,
  editable,
  tossMaterialTargetManuals,
  open,
  toggleOpen,
  setValue,
  getValues,
  register,
  control,
}: ServiceSpecTabGroupProps) {
  return (
    <>
      <h3 className="mdh2">
        <span>
          {tossMaterialGroup.group_name}
          <button
            type="button"
            className="text-button"
            onClick={() => {
              // ▽や△を押すことで、詳細部分を広げたり閉じたりする
              toggleOpen();
            }}
          >
            <i className={`fa-solid fa-chevron-${open ? 'down' : 'up'}`} />
          </button>
        </span>
      </h3>
      <SlideToggle open={open}>
        <div id="master21" className="masterfr2">
          {(tossMaterialGroup.toss_material_subgroups ?? []).map(
            (y, j) =>
              tossMaterialGroup.toss_material_group_id != null && (
                <ServiceSpecTabTable
                  productionId={productionId}
                  productionCategoryId={productionCategoryId}
                  tossMaterialGroupId={tossMaterialGroup.toss_material_group_id}
                  editable={editable}
                  key={y.toss_material_subgroup_id}
                  tossMaterialSubGroup={y}
                  i={i}
                  j={j}
                  tossMaterialTargetManuals={tossMaterialTargetManuals}
                  register={register}
                  setValue={setValue}
                  getValues={getValues}
                  control={control}
                />
              )
          )}
        </div>
      </SlideToggle>
    </>
  );
}

/**
 * フォームの初期値を取得
 * @param tree
 * @returns
 */
const getDefaultValues = (tree: TossMaterialCategoryTree): { toss_materials: TossMaterial[][][] } => {
  const result: TossMaterial[][][] = [];
  (tree.toss_material_groups ?? []).forEach((x) => {
    const temp: TossMaterial[][] = [];
    (x.toss_material_subgroups ?? []).forEach((y) => {
      temp.push(y.toss_materials ?? []);
    });
    result.push(temp);
  });
  return { toss_materials: result };
};

interface ServiceSpecTabProps {
  productionId: number;
  tree: TossMaterialCategoryTree;
  masters: ProductionMasters;
  editable: boolean;
  save: (tossMaterialList: TossMaterial[]) => Promise<void>;
  edit: () => Promise<void>;
  cancel: () => Promise<void>;
  setDisplayValidationError: (next: boolean) => void;
}

export interface ServiceSpecTabHandler {
  validate: () => Promise<boolean>; // バリデーションの実行
  submit: () => Promise<void>; // フォームの送信
  refresh: () => void; // フォームをリフレッシュ
}

/**
 * トス資料サービススペックタブ
 */
const ServiceSpecTab = forwardRef<ServiceSpecTabHandler, ServiceSpecTabProps>(
  ({ productionId, tree, masters, editable, save, edit, cancel, setDisplayValidationError }, ref) => {
    // バリデーション用のスキーマ
    const schema = yup.object().shape({
      toss_materials: yup
        .array()
        .required()
        .of(
          yup
            .array()
            .required()
            .of(
              yup
                .array()
                .required()
                .of(
                  yup.object().shape({
                    toss_material_id: yup
                      .number()
                      .nullable()
                      .transform((v) => (Number.isNaN(Number(v)) ? null : Number(v)))
                      .default(null),
                    toss_material_production_category_id: yup.number().required(),
                    toss_material_group_id: yup.number().required(),
                    toss_material_subgroup_id: yup.number().required(),
                    toss_material_sending_material_id: yup
                      .number()
                      .nullable()
                      .transform(() => null)
                      .default(null),
                    toss_material_material_name_id: yup
                      .number()
                      .nullable()
                      .transform(() => null)
                      .default(null),
                    check: yup.boolean().required(),
                    toss_material_target_manual_id: yup
                      .number()
                      .nullable()
                      .transform((v) => (Number.isNaN(Number(v)) || Number(v) === 0 ? null : Number(v)))
                      .default(null),
                    sp: yup.string().required(),
                    rev: yup
                      .number()
                      .nullable()
                      .transform(() => null)
                      .default(null),
                    toss_material_schedule_id: yup
                      .number()
                      .nullable()
                      .transform(() => null)
                      .default(null),
                    comment: yup.string().nullable().default(null),
                    is_initial: yup.boolean().required(),
                    sort_order: yup.number().required(),
                    file1: yup
                      .object()
                      .nullable()
                      .shape({
                        toss_material_file_id: yup
                          .number()
                          .nullable()
                          .transform((v) => (v || v === 0 ? Number(v) : null)),
                        file_name: yup
                          .string()
                          .nullable()
                          .transform((v) => (v === '' ? null : String(v))),
                      })
                      .default(null),
                    file2: yup
                      .object()
                      .nullable()
                      .shape({
                        toss_material_file_id: yup
                          .number()
                          .nullable()
                          .transform((v) => (v || v === 0 ? Number(v) : null)),
                        file_name: yup
                          .string()
                          .nullable()
                          .transform((v) => (v === '' ? null : String(v))),
                      })
                      .default(null),
                    share_destination_productions: yup
                      .array()
                      .nullable()
                      .transform(() => null)
                      .default(null),
                    update_user_name: yup.string().nullable().default(null),
                    updated_on: yup.string().date().nullable().default(null),
                    issued_at: yup.string().date().nullable().default(null),
                  })
                )
            )
        ),
    });

    // フォームを利用できるようにする
    const { register, handleSubmit, setValue, getValues, control, reset } = useForm({
      defaultValues: getDefaultValues(tree),
      resolver: yupResolver(schema),
    });

    // 詳細部分が開いているグループの ID
    const [openGroupIds, setOpenGroupIds] = useState<Array<number | undefined>>([]);

    // バリデーション
    const validate = useCallback(async () => {
      let result = false as boolean;
      await handleSubmit(() => {
        result = true;
      })();
      return result;
    }, [handleSubmit]);

    // 最新データで表示し直し
    const refresh = useCallback(() => {
      reset(getDefaultValues(tree));
    }, [reset, tree]);

    // 保存ボタン押下時の動作
    const submit = useCallback(async () => {
      // バリデーション結果が有効だった場合
      const onValid = async (data: { toss_materials: TossMaterial[][][] }) => {
        // API 実行
        const tossMaterialList = data.toss_materials.flat().flat();
        await save(tossMaterialList);
      };

      // バリデーション結果が無効だった場合（エラーモーダルを表示）
      const onInvalid: SubmitErrorHandler<{ toss_materials: TossMaterial[][][] }> = () => {
        // バリデーションエラーをモーダルで表示
        setDisplayValidationError(true);
      };

      // フォーム送信時の処理を設定
      await handleSubmit(onValid, onInvalid)();
    }, [handleSubmit, save, setDisplayValidationError]);

    // 親コンポーネントから操作できるようにする
    useImperativeHandle(ref, () => ({
      validate,
      submit,
      refresh,
    }));

    // 必要なデータがない場合
    if (
      masters.toss_material_target_manuals == null ||
      tree.toss_material_production_category_id == null ||
      tree.toss_material_groups == null
    ) {
      return (
        <div id="tab2-content2" className="tab-content2">
          <NoDataComponent />
        </div>
      );
    }

    return (
      <div id="tab2-content2" className="tab-content2">
        <form>
          {/* 共通ナビ */}
          <InnerTabCommonNavi
            productionId={productionId}
            tabIndex={2 as number}
            editable={editable}
            open={() => setOpenGroupIds((tree.toss_material_groups ?? []).map((x) => x.toss_material_group_id))}
            close={() => setOpenGroupIds([])}
            edit={edit}
            cancel={cancel}
          />
          {tree.toss_material_groups.map((x, i) => {
            if (tree.toss_material_production_category_id == null || masters.toss_material_target_manuals == null) {
              return <React.Fragment key={x.toss_material_group_id} />;
            }

            return (
              <ServiceSpecTabGroup
                key={x.toss_material_group_id}
                tossMaterialGroup={x}
                open={openGroupIds.includes(x.toss_material_group_id)}
                productionId={productionId}
                i={i}
                productionCategoryId={tree.toss_material_production_category_id}
                editable={editable}
                tossMaterialTargetManuals={masters.toss_material_target_manuals}
                setValue={setValue}
                getValues={getValues}
                register={register}
                toggleOpen={() => {
                  setOpenGroupIds((preList) => {
                    if (x.toss_material_group_id == null) {
                      return preList;
                    }
                    if (preList.includes(x.toss_material_group_id)) {
                      return preList.filter((y) => y !== x.toss_material_group_id);
                    }
                    return [...preList, x.toss_material_group_id];
                  });
                }}
                control={control}
              />
            );
          })}
        </form>
      </div>
    );
  }
);

export default ServiceSpecTab;
